<script setup lang="ts">
import { watchEffect, computed, onMounted, nextTick, ref } from 'vue'

import { type QueueTrack, useQueue } from '~/composables/audio/queue'
import { useStore } from '~/store'
import useLogger from '~/composables/useLogger'
import { useStyleTag, useIntervalFn } from '@vueuse/core'

import { generateTrackCreditString } from '~/utils/utils'
import { generateThemeCss } from '~/theming/generateThemeCss'

import '~/style/funkwhale.scss'

import PlaylistModal from '~/components/playlists/PlaylistModal.vue'
import FilterModal from '~/components/moderation/FilterModal.vue'
import ReportModal from '~/components/moderation/ReportModal.vue'
import ServiceMessages from '~/components/ServiceMessages.vue'
import AudioPlayer from '~/components/audio/Player.vue'
import Queue from '~/components/Queue.vue'
import Sidebar from '~/shell/Sidebar.vue'
import ShortcutsModal from '~/shell/Shortcuts.vue'
import LanguagesModal from '~/shell/Language.vue'
import UploadModal from '~/shell/Upload.vue'
import Loader from '~/ui/Loader.vue'
import { fw } from './ui/attributes'
import { useDataStore } from './data'
import type { components } from './generated/types'

const initialTitle = ref('')

// Fake content
onMounted(async () => {
  initialTitle.value = document.title
  await nextTick()
  document.getElementById('fake-app')?.remove()
})

const logger = useLogger()
logger.debug('App setup()')

const store = useStore()

// Tracks
const { currentTrack } = useQueue()
// TODO: Remove duplicate entries for tab title
const getTrackInformationText = (track: QueueTrack | undefined, artists: components['schemas']['ArtistWithAlbums'][]) => {
  if (!track || !track.artistCredit) {
    return null
  }

  return `♫ ${track.title} – ${generateTrackCreditString(track.artistCredit, artists)} ♫`
}

// Update title whenever the current track or artists change
watchEffect(() => {
  const artistRefs = (currentTrack.value?.artistCredit ?? []).map(({ artist }) =>
    useDataStore().get_artist(artist.id)
  )
  const parts = [
    getTrackInformationText(
      currentTrack.value,
      artistRefs
        .map(ref => ref.value.data)
        .filter((artist): artist is components['schemas']['ArtistWithAlbums'] => !!artist)
    ),
    store.state.ui.pageTitle,
    initialTitle.value || 'Funkwhale'
  ]

  document.title = parts.filter(i => i).join(' – ')
})

// Generated instance theme, in @layer instance-theme (ranked below
// artist-theme -- see the @layer declaration in the <style> block below).
// Both theme layers lose to unlayered rules, so the hand-written custom_css
// override further down still wins over either.
useStyleTag(computed(() => `@layer instance-theme {\n${generateThemeCss({
  theme_primary_color: store.state.instance.settings.ui.theme_primary_color.value || null,
  theme_accent_color: store.state.instance.settings.ui.theme_accent_color.value || null,
  theme_background_color: store.state.instance.settings.ui.theme_background_color.value || null
})}\n}`))

// Styles
useStyleTag(computed(() => store.state.instance.settings.ui.custom_css.value))

// Fake content
onMounted(async () => {
  await nextTick()
  document.getElementById('fake-content')?.classList.add('loaded')
})

// Time ago
useIntervalFn(() => {
  // used to redraw ago dates every minute
  store.commit('ui/computeLastDate')
}, 1000 * 60)

// Fetch user data on startup
// NOTE: We're not checking if we're authenticated in the store,
//       because we want to learn if we are authenticated at all
store.dispatch('auth/fetchUser')
</script>

<template>
  <div class="funkwhale responsive">
    <Sidebar style="grid-area: sidebar;" />
    <RouterView
      v-slot="{ Component }"
      :class="[
        fw.color.defaultSolid,
        fw.padding.page,
        $style.layout
      ]"
      :style="{
        gridArea: 'main'
      }"
    >
      <Transition
        v-if="Component"
        mode="out-in"
      >
        <KeepAlive :max="10">
          <Suspense>
            <component :is="Component" />
            <template #fallback>
              <Loader />
            </template>
          </Suspense>
        </KeepAlive>
      </Transition>
      <transition name="queue">
        <Queue v-show="store.state.ui.queueFocused" />
      </transition>
    </RouterView>
  </div>
  <AudioPlayer class="funkwhale color-default-solid" />
  <ServiceMessages />
  <LanguagesModal />
  <ShortcutsModal />
  <PlaylistModal v-if="store.state.auth.authenticated" />
  <FilterModal v-if="store.state.auth.authenticated" />
  <ReportModal />
  <UploadModal v-if="store.state.auth.authenticated" />
</template>

<style scoped>
.responsive {
  display: grid !important;
  grid-template-columns: 300px minmax(0, 1fr);
  grid-template-rows: min-content auto 4rem;

  min-height: 100vh;

  grid-template-areas:
    "sidebar sidebar"
    "main main"
    "player player";

  @media screen and (min-width: 1024px) {
    grid-template-areas:
      "sidebar main"
      "sidebar main"
      "player player";
  }
}
</style>

<style>
/* Global @layer order. Both theme layers are generated at runtime by
   useStyleTag (above); declaring the order here -- statically, before either
   tag exists -- pins instance-theme < artist-theme regardless of which tag
   (re-)inserts into <head> first (layer order follows first mention, not
   source order). fw.css's own sanitize/document/system are listed first
   because this block is evaluated before fw.css (App.vue is imported earlier
   in main.ts); leaving them out would let fw.css's later bare @layer
   declaration rank them after our theme layers, and its default colours would
   then beat any override. Both theme layers still lose to unlayered rules --
   that's what keeps custom_css (below, unlayered) on top. */
@layer sanitize, document, system, instance-theme, artist-theme;

/* Make inert pages (behind modals) unscrollable */
body:has(#app[inert="true"]) {
  overflow: hidden;
}

body, html {
    /* Default color (sidebar and player) */
    background-color: color-mix(in oklab, var(--color-250) 20%, var(--color-200));
}
</style>

<style module>
.layout {
  /* Make space for the play bar */
  padding-bottom: 5rem;

  transition: all .3s;

  /* Recessed (less contrasty) color */
  background-color: var(--color-background-150)
}
</style>
